/**
 * CONFIGURAÇÕES — parametrização FUNCIONAL do INSPECT IA.
 *
 * Define COMO O SISTEMA FUNCIONA. Não define como ele APARECE: logo, cores,
 * fonte, tela de login, layout e template visual pertencem ao STUDIO e não
 * existem neste modelo — nem como campo "reservado".
 *
 * Três regras atravessam o arquivo:
 *
 * 1. PARAMETRIZAÇÃO ≠ REGRA DE SEGURANÇA. Auditoria, autorização no servidor,
 *    integridade do snapshot aprovado, histórico de aprovação, controle de
 *    acesso e validações essenciais NÃO são campos: estão em `LOCKED_RULES`,
 *    aparecem na tela travados e com o motivo, e não há caminho para
 *    desligá-los.
 * 2. CONFIGURAÇÃO SÓ APERTA, NUNCA AFROUXA o mínimo do domínio. Uma
 *    obrigatoriedade de END pode ser ACRESCENTADA (foto, croqui); a que o
 *    domínio já exige não pode ser retirada. A senha mínima nunca fica abaixo
 *    de 8.
 * 3. CONTROLE QUE NÃO MUDA NADA DIZ QUE NÃO MUDA. Cada campo tem em
 *    `SETTING_EFFECTS` onde é aplicado — ou a marca de que está só
 *    registrado. A tela mostra isso ao lado do campo.
 *
 * Mudar uma configuração nunca reescreve documento aprovado, PDF histórico,
 * snapshot, assinatura ou auditoria anterior: tudo o que é congelado foi
 * congelado com os valores da época, e número de documento é atribuído uma
 * única vez, na criação.
 */
import type { NotificationEvent } from "../notification/notification.js";
import type { InspectionModality, InspectionNature } from "../inspection/inspection-domain.js";
import {
  DEFAULT_NUMBERING,
  NUMBERED_DOCUMENT_TYPES,
  validateNumberingConfig,
  type NumberedDocumentType,
  type NumberingConfig
} from "./document-numbering.js";

// ═════════════════════════════════════════════════════════════ seções

export type SettingsSectionId =
  | "general" | "numbering" | "workflow" | "inspection" | "end" | "integrity"
  | "import" | "notification" | "files" | "security" | "signature" | "integration";

export const SETTINGS_TABS: ReadonlyArray<{ id: string; number: string; label: string; section: SettingsSectionId | "diagnostics" | "history" | "danger_zone" }> = [
  { id: "geral", number: "01", label: "Geral", section: "general" },
  { id: "numeracao", number: "02", label: "Numeração Documental", section: "numbering" },
  { id: "workflow", number: "03", label: "Workflow e Aprovação", section: "workflow" },
  { id: "inspecoes", number: "04", label: "Inspeções", section: "inspection" },
  { id: "end", number: "05", label: "END", section: "end" },
  { id: "integridade", number: "06", label: "Integridade / AVR", section: "integrity" },
  { id: "importacoes", number: "07", label: "Importações", section: "import" },
  { id: "notificacoes", number: "08", label: "Notificações", section: "notification" },
  { id: "arquivos", number: "09", label: "Arquivos", section: "files" },
  { id: "seguranca", number: "10", label: "Segurança", section: "security" },
  { id: "assinatura", number: "11", label: "Assinatura Digital / ICP", section: "signature" },
  { id: "integracoes", number: "12", label: "Integrações", section: "integration" },
  { id: "diagnostico", number: "13", label: "Diagnóstico", section: "diagnostics" },
  { id: "historico", number: "14", label: "Histórico", section: "history" },
  { id: "zonaderisco", number: "15", label: "Zona de Risco / Reset", section: "danger_zone" }
];

/** Seções cuja alteração exige MOTIVO — mudam numeração, acesso ou assinatura. */
export const SECTIONS_REQUIRING_REASON: ReadonlySet<SettingsSectionId> = new Set(["numbering", "security", "signature", "integration", "import"]);

// ═════════════════════════════════════════════════════════════ 01 GERAL

export const TIMEZONE_OPTIONS = ["America/Sao_Paulo", "America/Manaus", "America/Cuiaba", "America/Belem", "America/Fortaleza", "America/Recife", "America/Noronha", "America/Rio_Branco", "UTC"] as const;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface GeneralSettings {
  language: "pt-BR";
  timezone: string;
  dateFormat: "DD/MM/AAAA" | "AAAA-MM-DD";
  timeFormat: "24H" | "12H";
  decimalSeparator: "," | ".";
  thicknessUnit: "mm" | "pol";
  pressureUnit: "kgf/cm²" | "bar" | "MPa" | "psi";
  temperatureUnit: "°C" | "°F";
  pageSize: number;
  autosaveEnabled: boolean;
  /** Espera depois da última digitação antes de gravar (o autosave é por inatividade, não por relógio). */
  autosaveDelaySeconds: number;
  confirmBeforeFinalize: boolean;
}

// ═════════════════════════════════════════════════════════ 04 INSPEÇÕES

export interface InspectionSettings {
  /** HERDAR = usa o de Geral. */
  autosaveMode: "HERDAR" | "LIGADO" | "DESLIGADO";
  autosaveDelaySeconds: number | null;
  confirmBeforeFinalize: "HERDAR" | "SIM" | "NAO";
  draftRecovery: boolean;
  /** EXTRAORDINÁRIA não entra: exige motivo, e um padrão preencheria a natureza sem o motivo. */
  defaultNature: "" | Exclude<InspectionNature, "EXTRAORDINARY">;
  defaultModality: "" | InspectionModality;
  warnIncomplete: boolean;
}

// ════════════════════════════════════════════════════════════════ 05 END

export type EndMethod = "ME" | "LP" | "PM" | "USC" | "BSCAN";
export const END_METHODS: readonly EndMethod[] = ["ME", "LP", "PM", "USC", "BSCAN"];
export const END_METHOD_LABELS: Record<EndMethod, string> = { ME: "Medição de Espessura", LP: "Líquido Penetrante", PM: "Partículas Magnéticas", USC: "Ultrassom Convencional", BSCAN: "B-Scan / C-Scan" };

export interface EndMethodSettings {
  /** Só a REFERÊNCIA ao cadastro oficial (Procedimentos / Catálogo técnico). Nenhum cadastro paralelo. */
  defaultProcedureId: string | null;
  defaultNormId: string | null;
  defaultCriteriaId: string | null;
  /** Obrigatoriedades ACRESCENTADAS ao mínimo do domínio. */
  requirePhoto: boolean;
  requireCroqui: boolean;
}

export type EndSettings = Record<EndMethod, EndMethodSettings>;

/** O que o domínio de cada método já exige — mostrado travado; configuração não retira. */
export const END_DOMAIN_REQUIREMENTS: Record<EndMethod, readonly string[]> = {
  ME: ["Aparelho de medição", "Procedimento", "Pontos medidos", "Inspetor e PLH signatários"],
  LP: ["Procedimento, norma e critério", "Consumíveis (penetrante, removedor, revelador)", "Resultados com laudo", "Inspetor e PLH signatários"],
  PM: ["Procedimento, norma e critério", "Yoke / equipamento", "Partículas", "Resultados com laudo", "Inspetor e PLH signatários"],
  USC: ["Procedimento, norma e critério", "Aparelho, transdutor e bloco de calibração", "Acoplante", "Resultados com laudo", "Inspetor e PLH signatários"],
  BSCAN: ["Procedimento, norma e critério", "Aparelho, transdutor e bloco de calibração", "Acoplante", "Áreas de varredura e resultados com laudo", "Inspetor e PLH signatários"]
};

// ════════════════════════════════════════════════════ 06 INTEGRIDADE / AVR

export interface IntegritySettings {
  thicknessDecimals: number;
  corrosionRateDecimals: number;
  remainingLifeDecimals: number;
  roundingMode: "HALF_UP" | "HALF_EVEN";
  corrosionRateUnit: "mm/ano";
  remainingLifeUnit: "anos";
}

/**
 * Arredondamento SÓ DE EXIBIÇÃO. O número calculado nunca passa por aqui
 * antes de ser gravado ou usado noutro cálculo: 0,253784 continua 0,253784
 * internamente e aparece "0,254 mm/ano".
 */
export function formatForDisplay(value: number | null | undefined, decimals: number, mode: IntegritySettings["roundingMode"] = "HALF_UP"): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const factor = 10 ** decimals;
  const scaled = value * factor;
  let rounded: number;
  if (mode === "HALF_EVEN") {
    const floor = Math.floor(scaled);
    const diff = scaled - floor;
    const EPS = 1e-9;
    if (Math.abs(diff - 0.5) < EPS) rounded = floor % 2 === 0 ? floor : floor + 1;
    else rounded = Math.round(scaled);
  } else {
    rounded = Math.sign(scaled) * Math.round(Math.abs(scaled) + 1e-9);
  }
  return (rounded / factor).toFixed(decimals).replace(".", ",");
}

// ═════════════════════════════════════════════════════════ 07 IMPORTAÇÕES

export interface ImportAlias {
  /** Campo de destino do cadastro (chave do mapeamento de importação). */
  field: string;
  alias: string;
}

export type ImportConflictPolicy = "PREENCHER_VAZIOS_SINALIZAR_CONFLITO" | "ATUALIZAR_EXISTENTES" | "PRESERVAR_EDICAO_MANUAL" | "SOLICITAR_CONFIRMACAO";

export const IMPORT_CONFLICT_POLICIES: ReadonlyArray<{ value: ImportConflictPolicy; label: string; available: boolean; reason: string }> = [
  { value: "PREENCHER_VAZIOS_SINALIZAR_CONFLITO", label: "Preencher somente vazios e sinalizar conflito", available: true, reason: "Comportamento recomendado: nunca sobrescreve um valor já cadastrado; divergência vira CONFLITO para a pessoa decidir." },
  { value: "ATUALIZAR_EXISTENTES", label: "Atualizar existentes", available: false, reason: "Sobrescreveria valores cadastrados sem conferência — não implementado de propósito." },
  { value: "PRESERVAR_EDICAO_MANUAL", label: "Preservar edição manual", available: false, reason: "Exige saber qual campo foi editado à mão; o cadastro ainda não registra a origem de cada campo." },
  { value: "SOLICITAR_CONFIRMACAO", label: "Solicitar confirmação", available: false, reason: "Já é o que acontece com os CONFLITOS da opção recomendada: nada divergente é gravado sem a pessoa ver." }
];

export interface ImportSettings {
  dateFormat: "DD/MM/AAAA" | "AAAA-MM-DD";
  decimalSeparator: "AUTO" | "," | ".";
  /** Marcadores ACRESCENTADOS aos que já valem como ausência de dado (NÃO INFORMADO, N/A, N/D, -). */
  extraEmptyMarkers: string[];
  aliases: ImportAlias[];
  conflictPolicy: ImportConflictPolicy;
}

// ═══════════════════════════════════════════════════════ 08 NOTIFICAÇÕES

export type NotificationSettingEvent =
  | "SUBMITTED" | "CORRECTION_REQUESTED" | "CORRECTION_RESOLVED" | "APPROVED" | "HOLD" | "ASSIGNMENT" | "RECOMMENDATION_CREATED"
  | "RECOMMENDATION_DUE" | "CALIBRATION_DUE" | "INSTRUMENT_DUE" | "DEADLINE";

export const NOTIFICATION_SETTING_EVENTS: ReadonlyArray<{ id: NotificationSettingEvent; label: string; emitted: boolean; note: string }> = [
  { id: "SUBMITTED", label: "Relatório enviado para análise / reenviado", emitted: true, note: "O reenvio após correção usa o mesmo evento do envio." },
  { id: "CORRECTION_REQUESTED", label: "Correção solicitada", emitted: true, note: "" },
  { id: "CORRECTION_RESOLVED", label: "Correção resolvida", emitted: true, note: "" },
  { id: "APPROVED", label: "Relatório aprovado", emitted: true, note: "" },
  { id: "HOLD", label: "HOLD (colocado, comentado, liberado)", emitted: true, note: "" },
  { id: "ASSIGNMENT", label: "Inspeção / Sub-OS atribuída", emitted: true, note: "" },
  { id: "RECOMMENDATION_CREATED", label: "Recomendação criada", emitted: true, note: "" },
  { id: "RECOMMENDATION_DUE", label: "Recomendação vencendo", emitted: false, note: "Ainda não existe rotina que detecte vencimento de recomendação." },
  { id: "CALIBRATION_DUE", label: "Calibração vencendo", emitted: false, note: "O vencimento aparece nas telas de END, mas nenhuma rotina gera o aviso." },
  { id: "INSTRUMENT_DUE", label: "Instrumento vencendo", emitted: false, note: "O vencimento aparece na tela de Instrumentos, mas nenhuma rotina gera o aviso." },
  { id: "DEADLINE", label: "Prazo próximo", emitted: false, note: "Não há prazo cadastrado por documento para disparar o aviso." }
];

/** Evento técnico do sistema → grupo configurável. SYSTEM_ALERT fica de fora: alerta do sistema não se desliga. */
export const NOTIFICATION_EVENT_GROUP: Partial<Record<NotificationEvent, NotificationSettingEvent>> = {
  DOCUMENT_SUBMITTED_TO_PLH: "SUBMITTED", AVR_SUBMITTED_FOR_PLH_REVIEW: "SUBMITTED", REPORT_FINALIZED: "SUBMITTED",
  DOCUMENT_PLH_CORRECTION_REQUESTED: "CORRECTION_REQUESTED", AVR_CORRECTION_REQUESTED: "CORRECTION_REQUESTED", CORRECTION_REQUESTED_BY_PLH: "CORRECTION_REQUESTED", REPORT_RETURNED: "CORRECTION_REQUESTED",
  CORRECTION_RESOLVED: "CORRECTION_RESOLVED",
  REPORT_APPROVED: "APPROVED", AVR_PLH_REVIEW_APPROVED: "APPROVED", DOCUMENT_PLH_APPROVED: "APPROVED",
  HOLD_PLACED: "HOLD", HOLD_ENGINEERING_COMMENTED: "HOLD", HOLD_RELEASED: "HOLD",
  INSPECTION_ASSIGNED: "ASSIGNMENT", SUB_OS_ASSIGNED: "ASSIGNMENT",
  RECOMMENDATION_CREATED: "RECOMMENDATION_CREATED"
};

export interface NotificationSettings {
  /** Canal interno por grupo de evento. E-mail não é integrado e não tem chave aqui. */
  internal: Record<NotificationSettingEvent, boolean>;
}

export function isNotificationEventEnabled(settings: NotificationSettings, event: NotificationEvent): boolean {
  const group = NOTIFICATION_EVENT_GROUP[event];
  if (!group) return true;
  return settings.internal[group] !== false;
}

// ════════════════════════════════════════════════════════════ 09 ARQUIVOS

export const IMAGE_EXTENSION_OPTIONS = ["png", "jpg", "jpeg", "webp"] as const;
export type ImageExtension = (typeof IMAGE_EXTENSION_OPTIONS)[number];

export interface FileSettings {
  maxImageMb: number;
  allowedImageExtensions: ImageExtension[];
  compressionEnabled: boolean;
  maxResolutionPx: number;
  imageQuality: number;
  /** null = sem limite. */
  maxPhotosPerReport: number | null;
  maxAttachmentsPerReport: number | null;
}

const MIME_OF: Record<ImageExtension, string> = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp" };

/** Aceita o arquivo pela extensão E pelo tipo declarado — os dois precisam estar na lista. */
export function isImageAllowed(settings: Pick<FileSettings, "allowedImageExtensions">, fileName: string, mimeType: string): boolean {
  const ext = (fileName.split(".").pop() || "").toLowerCase() as ImageExtension;
  if (!settings.allowedImageExtensions.includes(ext)) return false;
  return settings.allowedImageExtensions.some(e => MIME_OF[e] === mimeType);
}

// ════════════════════════════════════════════════════════════ 10 SEGURANÇA

export interface SecuritySettings {
  idleTimeoutMinutes: number;
  sessionMaxHours: number;
  loginMaxAttempts: number;
  lockoutMinutes: number;
  reauthForCriticalActions: boolean;
  passwordMinLength: number;
  passwordRequireUpper: boolean;
  passwordRequireLower: boolean;
  passwordRequireDigit: boolean;
  passwordRequireSymbol: boolean;
}

/** Piso absoluto: é o mínimo que o sistema já exigia antes desta tela. Configuração não desce disso. */
export const PASSWORD_MIN_LENGTH_FLOOR = 8;

/** Problemas da senha diante da política vigente. Vazio = aceita. Nunca devolve a senha. */
export function passwordPolicyViolations(policy: SecuritySettings, password: string): string[] {
  const problems: string[] = [];
  const min = Math.max(PASSWORD_MIN_LENGTH_FLOOR, policy.passwordMinLength);
  if (password.length < min) problems.push(`ter pelo menos ${min} caracteres`);
  if (policy.passwordRequireUpper && !/[A-ZÀ-Ý]/.test(password)) problems.push("ter uma letra maiúscula");
  if (policy.passwordRequireLower && !/[a-zß-ÿ]/.test(password)) problems.push("ter uma letra minúscula");
  if (policy.passwordRequireDigit && !/\d/.test(password)) problems.push("ter um número");
  if (policy.passwordRequireSymbol && !/[^A-Za-zÀ-ÿ0-9]/.test(password)) problems.push("ter um símbolo");
  return problems;
}

// ═══════════════════════════════════════════════ 11 ASSINATURA DIGITAL / ICP

export interface SignatureSettings {
  company: { legalName: string; tradeName: string; /** Só dígitos. A máscara é da apresentação. */ cnpj: string };
  service: {
    name: string;
    provider: string;
    environment: "HOMOLOGACAO" | "PRODUCAO";
    endpoint: string;
    clientId: string;
    certificateRef: string;
    format: "PADES";
    timestamp: boolean;
    postValidation: boolean;
    status: "ATIVO" | "INATIVO";
  };
  /**
   * O SEGREDO NUNCA ESTÁ AQUI. Este registro só guarda a máscara
   * ("••••••••A72C") e quando foi trocado; o valor fica no cofre do servidor.
   */
  secretMask: string | null;
  secretUpdatedAt: string | null;
  lastTest: IntegrationTestResult | null;
}

export function normalizeCnpj(value: string | null | undefined): string {
  return String(value ?? "").replace(/\D/g, "");
}

/** Dígitos verificadores do CNPJ (módulo 11). Aceita com ou sem máscara. */
export function isValidCnpj(value: string | null | undefined): boolean {
  const cnpj = normalizeCnpj(value);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  const digit = (len: number) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + Number(cnpj[i]) * w, 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return digit(12) === Number(cnpj[12]) && digit(13) === Number(cnpj[13]);
}

export function formatCnpj(value: string | null | undefined): string {
  const d = normalizeCnpj(value);
  if (d.length !== 14) return d;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** "••••••••A72C" — só os 4 últimos, e só quando o segredo tem tamanho para isso. */
export function maskSecret(secret: string): string {
  const tail = secret.length >= 8 ? secret.slice(-4) : "";
  return `••••••••${tail}`;
}

// ════════════════════════════════════════════════════════ 12 INTEGRAÇÕES

export interface IntegrationTestResult {
  at: string;
  ok: boolean;
  status: string;
  message: string;
  by: string;
}

export interface IntegrationSettings {
  lastTests: Record<string, IntegrationTestResult>;
}

// ═══════════════════════════════════════════════════════════ registro

export interface SystemSettings {
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  general: GeneralSettings;
  numbering: Record<NumberedDocumentType, NumberingConfig>;
  inspection: InspectionSettings;
  end: EndSettings;
  integrity: IntegritySettings;
  import: ImportSettings;
  notification: NotificationSettings;
  files: FileSettings;
  security: SecuritySettings;
  signature: SignatureSettings;
  integration: IntegrationSettings;
}

export type EditableSection = Exclude<SettingsSectionId, "workflow">;

const endDefaults = (): EndMethodSettings => ({ defaultProcedureId: null, defaultNormId: null, defaultCriteriaId: null, requirePhoto: false, requireCroqui: false });

/** Padrões = o comportamento que o sistema já tinha. Instalar a tela não muda nada. */
export function defaultSystemSettings(): SystemSettings {
  return {
    version: 0,
    updatedAt: null,
    updatedBy: null,
    general: {
      language: "pt-BR", timezone: "America/Sao_Paulo", dateFormat: "DD/MM/AAAA", timeFormat: "24H", decimalSeparator: ",",
      thicknessUnit: "mm", pressureUnit: "kgf/cm²", temperatureUnit: "°C", pageSize: 25,
      autosaveEnabled: true, autosaveDelaySeconds: 1, confirmBeforeFinalize: true
    },
    numbering: Object.fromEntries(NUMBERED_DOCUMENT_TYPES.map(t => [t, { ...DEFAULT_NUMBERING[t] }])) as Record<NumberedDocumentType, NumberingConfig>,
    inspection: { autosaveMode: "HERDAR", autosaveDelaySeconds: null, confirmBeforeFinalize: "HERDAR", draftRecovery: true, defaultNature: "", defaultModality: "", warnIncomplete: true },
    end: { ME: endDefaults(), LP: endDefaults(), PM: endDefaults(), USC: endDefaults(), BSCAN: endDefaults() },
    integrity: { thicknessDecimals: 2, corrosionRateDecimals: 3, remainingLifeDecimals: 1, roundingMode: "HALF_UP", corrosionRateUnit: "mm/ano", remainingLifeUnit: "anos" },
    import: { dateFormat: "DD/MM/AAAA", decimalSeparator: "AUTO", extraEmptyMarkers: [], aliases: [], conflictPolicy: "PREENCHER_VAZIOS_SINALIZAR_CONFLITO" },
    notification: {
      internal: {
        SUBMITTED: true, CORRECTION_REQUESTED: true, CORRECTION_RESOLVED: true, APPROVED: true, HOLD: true, ASSIGNMENT: true, RECOMMENDATION_CREATED: true,
        RECOMMENDATION_DUE: false, CALIBRATION_DUE: false, INSTRUMENT_DUE: false, DEADLINE: false
      }
    },
    files: { maxImageMb: 4, allowedImageExtensions: ["png", "jpg", "jpeg", "webp"], compressionEnabled: false, maxResolutionPx: 4096, imageQuality: 85, maxPhotosPerReport: null, maxAttachmentsPerReport: null },
    security: {
      idleTimeoutMinutes: 60, sessionMaxHours: 12, loginMaxAttempts: 5, lockoutMinutes: 15, reauthForCriticalActions: false,
      passwordMinLength: 8, passwordRequireUpper: false, passwordRequireLower: false, passwordRequireDigit: false, passwordRequireSymbol: false
    },
    signature: {
      company: { legalName: "", tradeName: "", cnpj: "" },
      service: { name: "", provider: "", environment: "HOMOLOGACAO", endpoint: "", clientId: "", certificateRef: "", format: "PADES", timestamp: true, postValidation: true, status: "INATIVO" },
      secretMask: null, secretUpdatedAt: null, lastTest: null
    },
    integration: { lastTests: {} }
  };
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/** Mescla em profundidade só objetos simples; arrays e escalares do gravado substituem o padrão. */
function deepMerge<T>(defaults: T, stored: unknown): T {
  if (!isObject(defaults) || !isObject(stored)) return (stored === undefined ? defaults : stored) as T;
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const [key, value] of Object.entries(stored)) {
    const def = (defaults as Record<string, unknown>)[key];
    out[key] = isObject(def) && isObject(value) ? deepMerge(def, value) : value;
  }
  return out as T;
}

/**
 * Registro gravado + padrões. Um campo acrescentado numa versão nova do
 * sistema nasce com o padrão em vez de `undefined`; um campo que deixou de
 * existir é descartado na próxima gravação.
 */
export function normalizeSystemSettings(stored: unknown): SystemSettings {
  const merged = deepMerge(defaultSystemSettings(), stored ?? {});
  // Nada de identidade visual no registro, mesmo que um payload antigo a traga.
  for (const forbidden of ["logo", "colors", "theme", "font", "loginScreen", "layout", "branding"]) delete (merged as unknown as Record<string, unknown>)[forbidden];
  return merged;
}

// ═══════════════════════════════════════════════════════ validação

const intIn = (v: unknown, min: number, max: number) => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;
const oneOf = <T>(v: unknown, list: readonly T[]) => list.includes(v as T);

/** Erros por seção. Vazio = pode gravar. */
export function validateSection(section: EditableSection, value: unknown): string[] {
  const e: string[] = [];
  if (!isObject(value)) return ["Conteúdo da seção inválido."];
  const v = value as Record<string, any>;
  switch (section) {
    case "general":
      if (v["language"] !== "pt-BR") e.push("Idioma: só Português (Brasil) está disponível.");
      if (!oneOf(v["timezone"], TIMEZONE_OPTIONS)) e.push("Fuso horário fora da lista.");
      if (!oneOf(v["dateFormat"], ["DD/MM/AAAA", "AAAA-MM-DD"])) e.push("Formato de data inválido.");
      if (!oneOf(v["timeFormat"], ["24H", "12H"])) e.push("Formato de hora inválido.");
      if (!oneOf(v["decimalSeparator"], [",", "."])) e.push("Separador decimal inválido.");
      if (!oneOf(v["thicknessUnit"], ["mm", "pol"])) e.push("Unidade de espessura inválida.");
      if (!oneOf(v["pressureUnit"], ["kgf/cm²", "bar", "MPa", "psi"])) e.push("Unidade de pressão inválida.");
      if (!oneOf(v["temperatureUnit"], ["°C", "°F"])) e.push("Unidade de temperatura inválida.");
      if (!oneOf(v["pageSize"], PAGE_SIZE_OPTIONS)) e.push("Registros por página: 10, 25, 50 ou 100.");
      if (typeof v["autosaveEnabled"] !== "boolean") e.push("Autosave: informe ligado ou desligado.");
      if (!intIn(v["autosaveDelaySeconds"], 1, 120)) e.push("Espera do autosave: de 1 a 120 segundos.");
      if (typeof v["confirmBeforeFinalize"] !== "boolean") e.push("Confirmação antes de finalizar: informe sim ou não.");
      break;
    case "numbering":
      for (const type of NUMBERED_DOCUMENT_TYPES) {
        const cfg = v[type];
        if (!isObject(cfg)) { e.push(`${type}: configuração ausente.`); continue; }
        e.push(...validateNumberingConfig({ ...(cfg as unknown as NumberingConfig), documentType: type }));
      }
      break;
    case "inspection":
      if (!oneOf(v["autosaveMode"], ["HERDAR", "LIGADO", "DESLIGADO"])) e.push("Autosave da inspeção inválido.");
      if (v["autosaveDelaySeconds"] !== null && !intIn(v["autosaveDelaySeconds"], 1, 120)) e.push("Espera do autosave da inspeção: de 1 a 120 segundos, ou herdar.");
      if (!oneOf(v["confirmBeforeFinalize"], ["HERDAR", "SIM", "NAO"])) e.push("Confirmação da inspeção inválida.");
      if (!oneOf(v["defaultNature"], ["", "INITIAL", "PERIODIC"])) e.push("Natureza padrão: Inicial, Periódica ou nenhuma (Extraordinária exige motivo e não pode ser padrão).");
      if (!oneOf(v["defaultModality"], ["", "INTERNAL", "EXTERNAL", "GENERAL"])) e.push("Modalidade padrão inválida.");
      for (const k of ["draftRecovery", "warnIncomplete"]) if (typeof v[k] !== "boolean") e.push(`${k}: informe sim ou não.`);
      break;
    case "end":
      for (const m of END_METHODS) {
        const cfg = v[m];
        if (!isObject(cfg)) { e.push(`${m}: configuração ausente.`); continue; }
        for (const k of ["defaultProcedureId", "defaultNormId", "defaultCriteriaId"]) if (cfg[k] !== null && typeof cfg[k] !== "string") e.push(`${m}: ${k} inválido.`);
        for (const k of ["requirePhoto", "requireCroqui"]) if (typeof cfg[k] !== "boolean") e.push(`${m}: ${k} deve ser sim ou não.`);
      }
      break;
    case "integrity":
      if (!intIn(v["thicknessDecimals"], 0, 4)) e.push("Casas decimais de espessura: 0 a 4.");
      if (!intIn(v["corrosionRateDecimals"], 1, 6)) e.push("Casas decimais da taxa de corrosão: 1 a 6.");
      if (!intIn(v["remainingLifeDecimals"], 0, 3)) e.push("Casas decimais da vida remanescente: 0 a 3.");
      if (!oneOf(v["roundingMode"], ["HALF_UP", "HALF_EVEN"])) e.push("Modo de arredondamento inválido.");
      if (v["corrosionRateUnit"] !== "mm/ano" || v["remainingLifeUnit"] !== "anos") e.push("As unidades da AVR são fixas pelo motor de cálculo (mm/ano, anos).");
      break;
    case "import": {
      if (!oneOf(v["dateFormat"], ["DD/MM/AAAA", "AAAA-MM-DD"])) e.push("Formato de data da importação inválido.");
      if (!oneOf(v["decimalSeparator"], ["AUTO", ",", "."])) e.push("Separador decimal da importação inválido.");
      if (!Array.isArray(v["extraEmptyMarkers"]) || v["extraEmptyMarkers"].some((m: unknown) => typeof m !== "string" || !m.trim() || m.length > 40)) e.push("Marcadores de vazio: textos de 1 a 40 caracteres.");
      const policy = IMPORT_CONFLICT_POLICIES.find(p => p.value === v["conflictPolicy"]);
      if (!policy) e.push("Política de conflito inválida.");
      else if (!policy.available) e.push(`Política "${policy.label}" não disponível: ${policy.reason}`);
      if (!Array.isArray(v["aliases"])) { e.push("Aliases inválidos."); break; }
      const seen = new Map<string, string>();
      for (const a of v["aliases"] as unknown[]) {
        if (!isObject(a) || typeof a["field"] !== "string" || typeof a["alias"] !== "string" || !a["alias"].trim() || !a["field"].trim()) { e.push("Cada alias precisa de campo de destino e título."); continue; }
        if (a["alias"].length > 80) e.push(`Alias "${a["alias"].slice(0, 20)}…" passa de 80 caracteres.`);
        const key = a["alias"].trim().toLowerCase();
        const previous = seen.get(key);
        if (previous && previous !== a["field"]) e.push(`O título "${a["alias"]}" aponta para dois campos (${previous} e ${a["field"]}) — um título só pode ter um destino.`);
        seen.set(key, a["field"]);
      }
      break;
    }
    case "notification": {
      const internal = v["internal"];
      if (!isObject(internal)) { e.push("Notificações inválidas."); break; }
      for (const ev of NOTIFICATION_SETTING_EVENTS) {
        if (typeof internal[ev.id] !== "boolean") e.push(`${ev.label}: informe ligado ou desligado.`);
        else if (!ev.emitted && internal[ev.id] === true) e.push(`${ev.label}: não pode ser ligado — ${ev.note}`);
      }
      break;
    }
    case "files":
      if (typeof v["maxImageMb"] !== "number" || v["maxImageMb"] < 0.5 || v["maxImageMb"] > 10) e.push("Tamanho máximo de imagem: de 0,5 a 10 MB.");
      if (!Array.isArray(v["allowedImageExtensions"]) || v["allowedImageExtensions"].length === 0 || v["allowedImageExtensions"].some((x: unknown) => !oneOf(x, IMAGE_EXTENSION_OPTIONS))) e.push("Extensões: escolha ao menos uma entre PNG, JPG, JPEG e WEBP.");
      if (typeof v["compressionEnabled"] !== "boolean") e.push("Compressão: informe sim ou não.");
      if (!intIn(v["maxResolutionPx"], 640, 8192)) e.push("Resolução máxima: de 640 a 8192 px.");
      if (!intIn(v["imageQuality"], 40, 100)) e.push("Qualidade de imagem: de 40 a 100.");
      for (const k of ["maxPhotosPerReport", "maxAttachmentsPerReport"]) if (v[k] !== null && !intIn(v[k], 1, 500)) e.push(`${k === "maxPhotosPerReport" ? "Limite de fotos" : "Limite de anexos"}: de 1 a 500, ou sem limite.`);
      break;
    case "security":
      if (!intIn(v["idleTimeoutMinutes"], 5, 480)) e.push("Inatividade: de 5 a 480 minutos.");
      if (!intIn(v["sessionMaxHours"], 1, 24)) e.push("Sessão máxima: de 1 a 24 horas.");
      if (!intIn(v["loginMaxAttempts"], 3, 20)) e.push("Tentativas de login: de 3 a 20.");
      if (!intIn(v["lockoutMinutes"], 1, 1440)) e.push("Bloqueio temporário: de 1 a 1440 minutos.");
      if (!intIn(v["passwordMinLength"], PASSWORD_MIN_LENGTH_FLOOR, 64)) e.push(`Tamanho mínimo da senha: de ${PASSWORD_MIN_LENGTH_FLOOR} a 64 — abaixo de ${PASSWORD_MIN_LENGTH_FLOOR} afrouxaria a regra que já vale.`);
      for (const k of ["reauthForCriticalActions", "passwordRequireUpper", "passwordRequireLower", "passwordRequireDigit", "passwordRequireSymbol"]) if (typeof v[k] !== "boolean") e.push(`${k}: informe sim ou não.`);
      break;
    case "signature": {
      const c = v["company"]; const s = v["service"];
      if (!isObject(c) || !isObject(s)) { e.push("Assinatura: empresa e serviço são obrigatórios."); break; }
      const cnpj = normalizeCnpj(c["cnpj"] as string);
      if (cnpj && !isValidCnpj(cnpj)) e.push("CNPJ inválido (dígitos verificadores não conferem).");
      if (String(c["legalName"] ?? "").length > 150 || String(c["tradeName"] ?? "").length > 150) e.push("Razão social / nome fantasia: até 150 caracteres.");
      if (!oneOf(s["environment"], ["HOMOLOGACAO", "PRODUCAO"])) e.push("Ambiente: Homologação ou Produção.");
      if (!oneOf(s["status"], ["ATIVO", "INATIVO"])) e.push("Status: Ativo ou Inativo.");
      if (s["format"] !== "PADES") e.push("Formato de assinatura: PAdES.");
      const endpoint = String(s["endpoint"] ?? "").trim();
      if (endpoint && !/^https:\/\/[^\s/$.?#].[^\s]*$/i.test(endpoint)) e.push("Endpoint: informe um endereço https:// válido.");
      if (s["status"] === "ATIVO") {
        if (!cnpj) e.push("Para ATIVAR a integração, informe o CNPJ da empresa titular.");
        if (!endpoint) e.push("Para ATIVAR a integração, informe o endpoint.");
        if (!String(s["clientId"] ?? "").trim()) e.push("Para ATIVAR a integração, informe o Client ID.");
        if (!String(s["provider"] ?? "").trim()) e.push("Para ATIVAR a integração, informe o provedor.");
      }
      for (const k of ["timestamp", "postValidation"]) if (typeof s[k] !== "boolean") e.push(`${k}: informe sim ou não.`);
      // Campos que nunca podem viajar nesta seção.
      for (const k of ["secret", "clientSecret", "privateKey", "pin", "password", "token"]) if (k in s || k in v) e.push("O segredo não é gravado junto com a configuração: use a ação própria de trocar segredo.");
      break;
    }
    case "integration":
      if (!isObject(v["lastTests"])) e.push("Resultados de teste inválidos.");
      break;
  }
  return e;
}

// ═════════════════════════════════════════════════════ regras travadas

export interface LockedRule {
  id: string;
  label: string;
  value: string;
  reason: string;
  area: "workflow" | "security" | "end" | "import" | "inspection";
}

export const LOCKED_RULES: readonly LockedRule[] = [
  { id: "audit", area: "security", label: "Registrar auditoria", value: "SIM", reason: "Auditoria não é parametrização: desligá-la apagaria a prova do que aconteceu." },
  { id: "backend-authorization", area: "security", label: "Autorização no servidor", value: "SIM", reason: "Toda ação é conferida no serviço; esconder botão nunca foi o controle." },
  { id: "access-control", area: "security", label: "Controle de acesso por permissão", value: "SIM", reason: "Permissões são administradas em Pessoas e Acessos; Configurações não tem matriz paralela." },
  { id: "plh-required", area: "workflow", label: "Exigir análise do PLH", value: "SIM", reason: "RI, ME, LP, PM, US, B-Scan e AVR exigem PLH (política única de aprovação). Um documento técnico sem responsável técnico não fecha." },
  { id: "correction", area: "workflow", label: "Permitir solicitar correção", value: "SIM", reason: "Sem devolução, o PLH só poderia aprovar um documento com erro. A devolução é por item e o servidor recusa motivo genérico." },
  { id: "resubmit", area: "workflow", label: "Permitir reenvio após correção", value: "SIM", reason: "É o caminho de volta da correção — sem ele o documento devolvido ficaria preso." },
  { id: "lock-approved", area: "workflow", label: "Bloquear documento aprovado", value: "SIM", reason: "Alterar um aprovado exige nova revisão; a aprovada permanece intacta." },
  { id: "snapshot", area: "workflow", label: "Gerar snapshot ao aprovar", value: "SIM", reason: "O PDF reimpresso precisa ser o que o PLH aprovou, mesmo que um cadastro mude depois." },
  { id: "approval-history", area: "workflow", label: "Histórico de aprovação", value: "SIM", reason: "Quem analisou, devolveu e aprovou fica na linha do tempo do documento e nunca é reescrito." },
  { id: "technical-review", area: "workflow", label: "Exigir revisão técnica", value: "SIM (é a análise do PLH)", reason: "A revisão técnica do sistema É a análise do PLH; não existe uma segunda etapa para ligar ou desligar." },
  { id: "essential-validation", area: "end", label: "Validações essenciais de cada método", value: "SIM", reason: "Configuração pode ACRESCENTAR exigências (foto, croqui); as do domínio não podem ser retiradas." },
  { id: "equipment-condition", area: "inspection", label: "Condição padrão do equipamento", value: "NÃO EXISTE", reason: "Condição (APTO / NÃO APTO / A CRITÉRIO DO CLIENTE) é juízo técnico do Engenheiro; um valor pré-preenchido aprovaria por omissão." },
  { id: "not-informed", area: "import", label: "\"NÃO INFORMADO\" é ausência de dado", value: "SIM", reason: "Importado como texto, viraria valor real impresso nos documentos." },
  { id: "import-preview", area: "import", label: "Preview antes de importar", value: "SIM", reason: "Nada é gravado sem a pessoa ver o que vai entrar, o que completa e o que conflita." }
];

// ═══════════════════════════════════════════════ o que cada campo controla

export interface SettingEffect {
  applied: boolean;
  /** Onde o valor é lido (aplicado) ou por que ainda não é. */
  where: string;
}

const REG = (why: string): SettingEffect => ({ applied: false, where: why });
const APP = (where: string): SettingEffect => ({ applied: true, where });

/** Chave: "seção.campo". Campos ausentes daqui são estrutura (sem efeito isolado). */
export const SETTING_EFFECTS: Readonly<Record<string, SettingEffect>> = {
  "general.language": REG("Só existe Português (Brasil)."),
  "general.timezone": REG("As datas seguem o relógio do computador de quem usa; o fuso configurado ainda não é aplicado."),
  "general.dateFormat": REG("As telas e os PDFs usam DD/MM/AAAA fixo."),
  "general.timeFormat": REG("As telas usam 24h fixo."),
  "general.decimalSeparator": REG("A digitação aceita vírgula e ponto; a exibição usa vírgula."),
  "general.thicknessUnit": REG("Os módulos gravam e calculam em mm; não há conversão de unidade."),
  "general.pressureUnit": REG("A unidade vem do cadastro de cada ativo."),
  "general.temperatureUnit": REG("Os módulos gravam em °C."),
  "general.pageSize": REG("Cada listagem tem a sua paginação."),
  "general.autosaveEnabled": APP("Relatórios ME, LP, AVR, PM, US-Convencional e B-Scan."),
  "general.autosaveDelaySeconds": APP("Relatórios ME, LP, AVR, PM, US-Convencional e B-Scan."),
  "general.confirmBeforeFinalize": REG("As confirmações de aprovar/finalizar continuam sempre ativas."),
  "numbering.*": APP("RI, ME, LP, PM, US-Convencional, B-Scan, AVR e Minuta — só documentos NOVOS."),
  "inspection.autosaveMode": REG("A Checklist tem gravação própria."),
  "inspection.autosaveDelaySeconds": REG("A Checklist tem gravação própria."),
  "inspection.confirmBeforeFinalize": REG("A Checklist mantém a confirmação própria."),
  "inspection.draftRecovery": REG("A Checklist já recupera o rascunho salvo."),
  "inspection.defaultNature": REG("A criação de Inspeção ainda não lê este padrão."),
  "inspection.defaultModality": REG("A criação de Inspeção ainda não lê este padrão."),
  "inspection.warnIncomplete": REG("A Checklist já avisa o que falta."),
  "end.defaultProcedureId": APP("Pré-seleção ao criar relatório PM, US-Convencional e B-Scan."),
  "end.defaultNormId": APP("Pré-seleção ao criar relatório PM, US-Convencional e B-Scan."),
  "end.defaultCriteriaId": APP("Pré-seleção ao criar relatório PM, US-Convencional e B-Scan."),
  "end.requirePhoto": APP("Validação dos relatórios PM, US-Convencional e B-Scan."),
  "end.requireCroqui": APP("Validação dos relatórios PM, US-Convencional e B-Scan."),
  "integrity.thicknessDecimals": REG("A AVR e o RI imprimem com as casas decimais do motor; a formatação configurável ainda não foi ligada aos PDFs."),
  "integrity.corrosionRateDecimals": REG("Idem — o cálculo mantém precisão total de qualquer forma."),
  "integrity.remainingLifeDecimals": REG("Idem."),
  "integrity.roundingMode": REG("Idem."),
  "import.dateFormat": REG("A importação reconhece as datas pelo próprio conteúdo."),
  "import.decimalSeparator": REG("A importação aceita vírgula e ponto."),
  "import.extraEmptyMarkers": APP("Importação da Lista de Linhas / Sistemas."),
  "import.aliases": APP("Importação da Lista de Linhas / Sistemas."),
  "import.conflictPolicy": APP("Importação — só a política recomendada está disponível."),
  "notification.internal": APP("Notificações internas geradas pelos módulos."),
  "files.maxImageMb": APP("Upload de fotos, croqui e imagens nos relatórios PM, US-Convencional e B-Scan."),
  "files.allowedImageExtensions": APP("Upload nos relatórios PM, US-Convencional e B-Scan."),
  "files.compressionEnabled": REG("Não existe compressão de imagem no sistema."),
  "files.maxResolutionPx": REG("Não existe redimensionamento de imagem no sistema."),
  "files.imageQuality": REG("Não existe recompressão de imagem no sistema."),
  "files.maxPhotosPerReport": APP("Upload de fotos nos relatórios PM, US-Convencional e B-Scan (documentos já gravados não são afetados)."),
  "files.maxAttachmentsPerReport": REG("Os anexos são por módulo; o limite geral ainda não é aplicado."),
  "security.idleTimeoutMinutes": REG("Não há expiração por inatividade."),
  "security.sessionMaxHours": REG("A sessão local vale 12h e a do servidor segue o serviço de sessão; o valor ainda não é lido."),
  "security.loginMaxAttempts": REG("Não há contagem de tentativas de login."),
  "security.lockoutMinutes": REG("Não há bloqueio temporário de conta."),
  "security.reauthForCriticalActions": REG("Não há reautenticação para ações críticas."),
  "security.passwordMinLength": APP("Criação de conta com senha, troca de senha e redefinição pelo Administrador."),
  "security.passwordRequireUpper": APP("Idem."),
  "security.passwordRequireLower": APP("Idem."),
  "security.passwordRequireDigit": APP("Idem."),
  "security.passwordRequireSymbol": APP("Idem."),
  "signature.*": REG("Nenhum provedor ICP está integrado: a configuração é validada e guardada, e o documento ainda é assinado pela imagem cadastrada do Profissional.")
};

export function effectOf(section: string, field: string): SettingEffect | null {
  return SETTING_EFFECTS[`${section}.${field}`] ?? SETTING_EFFECTS[`${section}.*`] ?? null;
}

// ═══════════════════════════════════════════════════════ diferença

export interface SettingChange {
  path: string;
  previous: unknown;
  next: unknown;
}

/** Caminhos folha que mudaram. Arrays comparados inteiros (a mudança é a lista). */
export function diffSection(previous: unknown, next: unknown, prefix: string): SettingChange[] {
  if (isObject(previous) && isObject(next)) {
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
    return [...keys].flatMap(k => diffSection(previous[k], next[k], `${prefix}.${k}`));
  }
  return JSON.stringify(previous) === JSON.stringify(next) ? [] : [{ path: prefix, previous: previous ?? null, next: next ?? null }];
}

// ═══════════════════════════════════════════════════════ histórico

export interface SettingsHistoryEntry {
  id: string;
  at: string;
  userId: string;
  userName: string;
  section: SettingsSectionId;
  path: string;
  previous: string;
  next: string;
  reason: string;
  auditEvent: string;
}

/** Status do histórico: a mudança mais recente de um campo é a VIGENTE; as anteriores foram SUBSTITUÍDAS. */
export function historyStatus(entries: readonly SettingsHistoryEntry[]): Map<string, "VIGENTE" | "SUBSTITUÍDA"> {
  const status = new Map<string, "VIGENTE" | "SUBSTITUÍDA">();
  const seen = new Set<string>();
  for (const entry of [...entries].sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id))) {
    status.set(entry.id, seen.has(entry.path) ? "SUBSTITUÍDA" : "VIGENTE");
    seen.add(entry.path);
  }
  return status;
}

export const AUDIT_EVENT_OF_SECTION: Record<SettingsSectionId, string> = {
  general: "SETTINGS_UPDATED", numbering: "SETTINGS_UPDATED", inspection: "SETTINGS_UPDATED", end: "SETTINGS_UPDATED",
  integrity: "SETTINGS_UPDATED", notification: "SETTINGS_UPDATED", files: "SETTINGS_UPDATED",
  workflow: "WORKFLOW_SETTINGS_UPDATED", import: "IMPORT_SETTINGS_UPDATED",
  signature: "SIGNATURE_SETTINGS_UPDATED", security: "SECURITY_SETTINGS_UPDATED", integration: "INTEGRATION_UPDATED"
};

/** Valor para o histórico: curto, legível, e nunca um segredo. */
export function historyValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "SIM" : "NÃO";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > 300 ? `${text.slice(0, 297)}...` : text;
}
